import React, {
  HTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import styled from '@emotion/styled'
import { Button, Popover, Text } from '@hhgtech/hhg-components/mantine'
import { Minus, Plus } from '@hhgtech/icons/core'
import {
  ActionIcon,
  Box,
  Flex,
  Image,
  Loader,
  rem,
  Tooltip,
} from '@mantine/core'
import { useIntl } from 'react-intl'

import { Container } from 'components/atoms/container'
import GPS from 'components/atoms/icons/GPS'
import ZoomOutMap from 'components/atoms/icons/ZoomOutMap'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetSvgPath } from 'interfaces/constants'
import { CareHospital, LatLngType } from 'interfaces/types'

import {
  ClinicLocatorSearchParams,
  getCommonQueryParams,
  GMAP_CENTER_LOCALE,
  GMAP_DEFAULT_ZOOM,
  sanitizeDuplicateClinics,
  sanitizeDuplicateDistricts,
  useClinics,
} from './clinicLocator.helpers'
import { ClinicLocatorMap } from './clinicLocatorMap'
import { ClinicLocatorSidebar } from './clinicLocatorSidebar'
import { ClinicLocatorContext } from './context'
import useStyles from './index.styles'
import { SelectedClinicMarker } from './SelectedClinicMarker'

export type PaginationType = {
  current: number
  first: number
  last: number
  next: number
  prev: number
  total_items: number
  items_per_page: number
}

export const DefaultPagination = {
  current: 1,
  first: 1,
  last: 1,
  next: 1,
  prev: 1,
  total_items: 0,
  items_per_page: 20,
}

export type DistricItem = {
  id: string
  name: string
  city_id: string
}

export type ClinicLocatorProps = {
  title?: string
  specialties?: string[]
  specialties_hashtag?: string
  defaultColor?: string
  funnelId?: string
  hideBooking?: boolean
  onGetBrowserLoc?: (v: LatLngType) => void
  infoPath?: string
  useMobileLayout?: boolean
} & HTMLAttributes<HTMLDivElement>

type ClinicData = {
  initialClinics: CareHospital[]
  initialPagination: PaginationType
  filter: DistricItem[]
}

const ClinicLocator: React.FC<ClinicLocatorProps> = ({
  title,
  specialties = [],
  specialties_hashtag,
  defaultColor,
  hideBooking,
  onGetBrowserLoc,
  infoPath,
  useMobileLayout,
  ...boxProps
}) => {
  const { formatMessage: f } = useIntl()
  const { classes } = useStyles({ useMobileLayout })

  const { locale } = useRouterExtend()
  const isMobileProp = useIsMobile(true)
  const currentUrl = useCurrentUrl()
  const backgroundColor = defaultColor

  const debounce = useRef<NodeJS.Timeout>()
  const mapRef = useRef<google.maps.Map | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const commonQueryParams: ClinicLocatorSearchParams = getCommonQueryParams({
    specialties,
  })
  const [queryParams, setQueryParams] = useState<ClinicLocatorSearchParams>(
    commonQueryParams,
  )

  const [searchLoading, setSearchLoading] = useState(false)
  const [searchClinics, setSearchClinics] = useState<CareHospital[]>([])
  const [searchDistrictData, setSearchDistrictData] = useState<DistricItem[]>(
    [],
  )
  const [displayClinics, setDisplayClinics] = useState<CareHospital[]>([])

  const [loadingLoc, setLoadingLoc] = useState(false)
  const [openedPopover, setOpenedPopover] = useState(false)
  const [isCurBrowserLoc, setIsCurBrowserLoc] = useState(false)

  const { loading, fetchClinics } = useClinics({ infoPath })
  const appendClinics = (clinics: CareHospital[]) => {
    setDisplayClinics((prevClinics) => {
      const newClinics = [...prevClinics, ...clinics]
      const newClinicsObj = newClinics.reduce((newList, item) => {
        newList[item.id] = item
        return newList
      }, {} as Record<number, CareHospital>)
      return Object.values(newClinicsObj)
    })
  }
  const [
    searchClinicsPagination,
    setSearchClinicsPagination,
  ] = useState<PaginationType>(DefaultPagination)
  const [selectedClinic, setSelectedClinic] = useState<CareHospital | null>(
    null,
  )
  const [clinicListData, setClinicListData] = useState<ClinicData | null>(null)
  const [currentLocation, setCurrentLocation] = useState<
    | {
        lat: number
        lng: number
      }
    | null
    | undefined
  >()
  const [searchValue, setSearchValue] = useState<string>('')
  const defaultCenter = GMAP_CENTER_LOCALE[locale]

  const {
    title: sidebarTitle,
    viewExpertsBtn: sidebarViewExpertsBtn,
  } = useMemo(
    () => ({
      title:
        title ||
        f({
          id: 'section.clinicLocator.title',
          defaultMessage: 'Explore the best Clinics for yourself',
        }),
      viewExpertsBtn: f({
        id: 'section.clinicLocator.viewExpertsBtn',
        defaultMessage: 'View Experts',
      }),
    }),
    [f, title],
  )
  const titleComp = useMemo(() => <TitleComp title={sidebarTitle} />, [
    sidebarTitle,
  ])

  const handleBoundMarkers = useCallback(
    (clinicLocations: CareHospital[] = []) => {
      if (clinicLocations.length == 0) {
        return false
      }
      if (mapRef.current) {
        const bounds = new window.google.maps.LatLngBounds()
        clinicLocations?.forEach((clinic) => {
          bounds.extend(
            new window.google.maps.LatLng(clinic.latitude, clinic.longitude),
          )
        })
        // Don't zoom in too far on only one marker
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
          const extendPoint1 = new google.maps.LatLng(
            bounds.getNorthEast().lat() + 0.005,
            bounds.getNorthEast().lng() + 0.005,
          )
          const extendPoint2 = new google.maps.LatLng(
            bounds.getNorthEast().lat() - 0.005,
            bounds.getNorthEast().lng() - 0.005,
          )
          bounds.extend(extendPoint1)
          bounds.extend(extendPoint2)
        }
        mapRef.current.fitBounds(bounds)
      }
    },
    [],
  )

  const handleReset = async (params?: Partial<ClinicLocatorSearchParams>) => {
    const { data } = await fetchClinics({ ...commonQueryParams, ...params })
    const { searches, districts, pagination } = data || {}
    setClinicListData({
      filter: sanitizeDuplicateDistricts(
        clinicListData?.filter?.concat(districts),
      ),
      initialClinics: searches,
      initialPagination: pagination || DefaultPagination,
    })
  }

  const handleFetch = async (
    latLng?: { lat: number | string; lng: number | string },
    isFetchNear = false,
    isCurLoc = false,
  ) => {
    const params = {
      ...commonQueryParams,
      ...(latLng && {
        latitude: latLng.lat,
        longitude: latLng.lng,
        ...(isFetchNear && {
          distance: 5,
        }),
      }),
    }
    setQueryParams(params)
    try {
      // search for all clinic
      const { data } = await fetchClinics(params)
      const { searches, districts, pagination } = data || {}
      setDisplayClinics(searches || [])
      setClinicListData((state) => ({
        ...state,
        initialClinics: searches,
        initialPagination: pagination || DefaultPagination,
        filter: sanitizeDuplicateDistricts(
          (clinicListData?.filter || [])
            ?.concat(districts)
            ?.concat(state?.filter || []),
        ),
      }))
      isCurLoc &&
        setTimeout(() => {
          setIsCurBrowserLoc(isCurLoc)
        }, 1000)
    } catch (e) {
      console.error(e)
    }
  }

  const handleFilter = async (districtId = '') => {
    const params: ClinicLocatorSearchParams = { ...queryParams, districtId }
    if (!params?.districtId) {
      delete params?.districtId
      delete params?.distance
    }
    setQueryParams(params)
    try {
      // search for clinic by name
      const { data } = await fetchClinics(params)
      const { searches, pagination } = data || {}
      setDisplayClinics(searches || [])
      setClinicListData(
        (state) =>
          ({
            ...state,
            initialClinics: searches,
            initialPagination: pagination || DefaultPagination,
          } as ClinicData),
      )
    } catch (e) {
      console.error(e)
    }
  }

  const handleSearch = async (keyword: string, byPassLength = false) => {
    const params = { ...queryParams, keyword }
    setSearchValue(keyword)
    setQueryParams(params)
    if (!keyword) {
      handleReset({ keyword })
      return
    }
    if (searchLoading || (keyword?.length < 3 && !byPassLength)) {
      return
    }

    if (debounce.current) clearTimeout(debounce.current)
    debounce.current = setTimeout(async () => {
      setSearchLoading(true)
      try {
        // search for clinic by name
        const { data } = await fetchClinics(params)
        const { searches, districts, pagination } = data
        const newFilter = sanitizeDuplicateDistricts(
          (clinicListData?.filter || []).concat(districts),
        )
        setSelectedClinic(null)
        setSearchClinics(searches)
        setSearchDistrictData(newFilter)
        setSearchClinicsPagination(pagination || DefaultPagination)
        searchRef.current?.focus()
      } catch (e) {
        console.error(e)
      }
      setSearchLoading(false)
      setTimeout(() => searchRef.current?.focus(), 0) // For sure focus the search input
    }, 250)
  }

  const handleChange = async (
    key: string,
    value?: string | number | boolean,
  ) => {
    if (searchLoading) return
    const newParams = { ...queryParams, [key]: value }
    if (debounce.current) {
      clearTimeout(debounce.current)
    }
    debounce.current = setTimeout(async () => {
      setSearchLoading(true)
      const { data } = await fetchClinics(newParams)
      const { searches, pagination, districts } = data
      const newClinics = sanitizeDuplicateClinics(
        (clinicListData?.initialClinics || [])?.concat(searches),
      )
      const newFilter = sanitizeDuplicateDistricts(
        (clinicListData?.filter || [])?.concat(districts),
      )
      setDisplayClinics(newClinics)
      setClinicListData((state) => ({
        ...state,
        initialClinics: newClinics,
        initialPagination: pagination || DefaultPagination,
        filter: newFilter,
      }))
      setSearchLoading(false)
    }, 250)
  }

  const handleMarkerClick = async (
    clinic: CareHospital,
    clearSearch = true,
  ) => {
    setSelectedClinic(clinic)
    if (clearSearch) {
      setSearchValue('')
    }
    setSearchClinics([])
    if (mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds()
      bounds.extend(
        new window.google.maps.LatLng(clinic.latitude, clinic.longitude),
      )
      // Don't zoom in too far on only one marker
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        const extendPoint1 = new google.maps.LatLng(
          bounds.getNorthEast().lat() + 0.01,
          bounds.getNorthEast().lng() + 0.01,
        )
        const extendPoint2 = new google.maps.LatLng(
          bounds.getNorthEast().lat() - 0.01,
          bounds.getNorthEast().lng() - 0.01,
        )
        bounds.extend(extendPoint1)
        bounds.extend(extendPoint2)
      }
      mapRef.current.fitBounds(bounds)
    }
    if (!clinicListData) {
      setDisplayClinics([clinic])
      const { data } = await fetchClinics({
        type: 'hospital',
        latitude: clinic.latitude,
        longitude: clinic.longitude,
        distance: 5,
        perPage: 50,
        showDistricts: 0,
      })
      const { searches } = data
      appendClinics(searches)
    }
  }

  const getCurrentLocation = () => {
    if (isCurBrowserLoc) return
    if (navigator.geolocation) {
      setLoadingLoc(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLoadingLoc(false)
          handleFetch(
            {
              lat: position.coords.latitude.toFixed(8),
              lng: position.coords.longitude.toFixed(8),
            },
            true,
            true,
          )
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
          onGetBrowserLoc?.({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
        },
        (err) => {
          console.log('Error getting user location', err)
          setLoadingLoc(false)
          if (err.code === 1) {
            setOpenedPopover(true)
          }
          return null
        },
      )
    } else {
      console.log('Geolocation is not supported by this browser.')
      return null
    }
  }

  const onZoom = useCallback(
    (step: number) => {
      return () => {
        const zoom = mapRef.current?.getZoom() || 15
        mapRef.current?.setZoom(zoom + step)
      }
    },
    [mapRef.current?.getZoom, mapRef.current?.setZoom],
  )

  useEffect(() => {
    if (mapRef.current && !!clinicListData) {
      if ((clinicListData?.initialClinics || []).length > 0) {
        handleBoundMarkers(clinicListData.initialClinics)
      } else {
        currentLocation && mapRef.current.setCenter(currentLocation)
      }
    }
  }, [clinicListData])

  useEffect(() => {
    if (mapRef.current && currentLocation) {
      const bounds = new window.google.maps.LatLngBounds()
      bounds.extend(
        new window.google.maps.LatLng(currentLocation.lat, currentLocation.lng),
      )
      // Don't zoom in too far on only one marker
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        const extendPoint1 = new google.maps.LatLng(
          bounds.getNorthEast().lat() + 0.01,
          bounds.getNorthEast().lng() + 0.01,
        )
        const extendPoint2 = new google.maps.LatLng(
          bounds.getNorthEast().lat() - 0.01,
          bounds.getNorthEast().lng() - 0.01,
        )
        bounds.extend(extendPoint1)
        bounds.extend(extendPoint2)
      }
      mapRef.current.fitBounds(bounds)
    }
  }, [currentLocation])

  // useEffect(() => {
  //   // debounce search
  //   searchValue === '' && setSearchClinics([])
  //   const timeoutId = setTimeout(async () => {
  //     handleSearch(searchValue)
  //   }, 500)
  //   return () => clearTimeout(timeoutId)
  // }, [searchValue])

  useEffect(() => {
    if (openedPopover) {
      const timeoutId = setTimeout(() => {
        setOpenedPopover(false)
      }, 3000)
      return () => clearTimeout(timeoutId)
    }
  }, [openedPopover])

  const isMobile = isMobileProp || useMobileLayout

  return (
    <ClinicLocatorContext.Provider value={{ isMobile }}>
      <StyledClinicLocatorContainer ref={containerRef} {...boxProps}>
        {isMobile && (
          <Container className="map-hhg-container">{titleComp}</Container>
        )}
        <Box
          sx={{
            position: 'relative',
            backgroundColor: '#fff',
            '.gm-style': {
              img: {
                maxHeight: 'none !important',
                maxWidth: '100%',
                objectFit: 'contain',
              },
              '.gm-style-iw-c': {
                padding: rem(12),
                borderRadius: rem(16),
                boxShadow:
                  '0px 0px 1px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06), 0px 4px 8px 0px rgba(0, 0, 0, 0.04)',
              },
              '.gm-style-iw > button': {
                right: '0 !important',
                top: '0 !important',
              },
            },
          }}
        >
          <Flex className={classes.wrapper} pos="relative">
            {/* Sidebar search & clinic */}
            <Flex
              id="clinicLocator--sidebar"
              direction="column"
              mah="100%"
              className={classes.sidebar}
            >
              <ClinicLocatorSidebar
                color={backgroundColor}
                loading={loading}
                extendComponent={
                  <>
                    {!isMobile && titleComp}
                    {
                      <ButtonComp
                        buttonText={sidebarViewExpertsBtn}
                        backgroundColor={backgroundColor}
                        specialties_hashtag={specialties_hashtag}
                        currentUrl={currentUrl}
                      />
                    }
                  </>
                }
                clinics={clinicListData?.initialClinics}
                pagination={clinicListData?.initialPagination}
                clinicSelected={selectedClinic}
                onSelectClinic={(clinic) => {
                  handleMarkerClick(clinic, false)
                  window.gtag?.('event', 'interaction_click', {
                    event_category: 'Service Connection',
                    event_action: 'Clinic Directory Click',
                    event_label: currentUrl,
                    hospital_id: clinic.id,
                    click_location: 'list',
                  })
                  setTimeout(() => {
                    document
                      .getElementById('clinicLocator--content')
                      ?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'center',
                      })
                  }, 100)
                }}
                searchLoading={searchLoading}
                searchValue={searchValue}
                searchData={searchClinics || []}
                onChangeSearch={handleSearch}
                onItemSubmitSearch={(item) => {
                  const params = { ...queryParams }
                  delete params.keyword
                  setSearchValue('')
                  setQueryParams(params)
                  appendClinics([item])
                  handleMarkerClick(item as any)
                  searchRef.current?.blur()
                  window.gtag?.('event', 'view_search_results', {
                    event_category: 'Service Connection',
                    event_action: 'Clinic Directory',
                    event_label: currentUrl,
                    search_term: searchValue,
                    search_result_shown: (
                      (searchClinics || []).length > 0
                    ).toString(),
                  })
                  window.gtag?.('event', 'interaction_click', {
                    event_category: 'Service Connection',
                    event_action: 'Clinic Directory Click',
                    event_label: currentUrl,
                    hospital_id: item.id,
                    click_location: 'search box',
                  })
                }}
                onSubmitSearch={(keyword) => {
                  if (searchClinics?.length) {
                    const params = { ...queryParams, keyword }
                    setSearchValue(keyword)
                    setQueryParams(params)
                    setSearchClinics([])
                    setDisplayClinics(searchClinics)
                    setTimeout(() => {
                      setClinicListData({
                        filter: sanitizeDuplicateDistricts(
                          (clinicListData?.filter || [])?.concat(
                            searchDistrictData,
                          ),
                        ),
                        initialClinics: searchClinics,
                        initialPagination: searchClinicsPagination,
                      })
                      searchRef?.current?.blur()
                    }, 200)
                    if (isMobile && containerRef?.current) {
                      window.scrollTo({
                        top:
                          containerRef.current.offsetTop +
                          containerRef.current.clientHeight +
                          64 -
                          window.outerHeight,
                        behavior: 'smooth',
                      })
                    }
                  }
                  window.gtag?.('event', 'view_search_results', {
                    event_category: 'Service Connection',
                    event_action: 'Clinic Directory',
                    event_label: currentUrl,
                    search_term: searchValue,
                    search_result_shown: (
                      (searchClinics || []).length > 0
                    ).toString(),
                  })
                }}
                filter={clinicListData?.filter}
                onFilter={(v) => handleFilter(v)}
                onChange={searchLoading ? undefined : handleChange}
                // Old Version
                searchRef={searchRef}
                queryParams={queryParams}
                onUpdateDisplayClinics={appendClinics}
              />
            </Flex>
            {/* Maps */}
            <Box id="clinicLocator--content" className={classes.content}>
              <ClinicLocatorMap
                ref={mapRef}
                onLoad={(map) => {
                  const lat = map.getCenter()?.lat()
                  const lng = map.getCenter()?.lng()
                  if (lat && lng) {
                    const isDefaultCenter =
                      defaultCenter?.lat === lat && defaultCenter?.lng === lng
                    const latLng = { lat, lng }
                    setCurrentLocation(latLng)
                    handleFetch(latLng, !isDefaultCenter)
                  }
                }}
                zoom={GMAP_DEFAULT_ZOOM}
                center={currentLocation || defaultCenter}
                onCenterChanged={() => setIsCurBrowserLoc(false)}
                markerItems={displayClinics}
                markerItemProps={{
                  onClick(clinic: CareHospital) {
                    handleMarkerClick(clinic)
                    window.gtag?.('event', 'interaction_click', {
                      event_category: 'Service Connection',
                      event_action: 'Clinic Directory Click',
                      event_label: currentUrl,
                      hospital_id: clinic.id,
                      click_location: 'map',
                    })
                  },
                }}
              >
                {selectedClinic && (
                  <SelectedClinicMarker
                    selectedClinic={selectedClinic}
                    hideBooking={hideBooking}
                  />
                )}
              </ClinicLocatorMap>
              {/* Zoom In/Zoom Out actions button */}
              <Flex
                direction="column"
                gap={0}
                pos="absolute"
                top="50%"
                right={rem(16)}
                className={classes.controlBtns}
              >
                <Tooltip
                  label={f({ id: 'clinicLocator.zoomOutToViewMore' })}
                  position="left"
                  withArrow
                >
                  <ActionIcon
                    variant="default"
                    className="zoomBtn"
                    onClick={() => {
                      handleBoundMarkers(displayClinics)
                    }}
                  >
                    <ZoomOutMap className="zoomIcon" />
                  </ActionIcon>
                </Tooltip>
                <ActionIcon
                  variant="default"
                  className="zoomBtn"
                  onClick={onZoom(1)}
                >
                  <Plus className="zoomIcon" />
                </ActionIcon>
                <ActionIcon
                  variant="default"
                  className="zoomBtn"
                  onClick={onZoom(-1)}
                >
                  <Minus className="zoomIcon" />
                </ActionIcon>
              </Flex>
              {/* Find Nearest Button */}
              <Flex
                pos="absolute"
                left="50%"
                bottom={rem(16)}
                sx={() => ({ zIndex: 0, transform: 'translate(-50%, 0)' })}
              >
                <Popover
                  position="top"
                  withArrow
                  arrowPosition="center"
                  shadow="md"
                  opened={openedPopover}
                  onChange={setOpenedPopover}
                  positionDependencies={['isMobile']}
                >
                  <Popover.Target>
                    <Button
                      className="pointer-event-child-none"
                      styles={(theme) => ({
                        root: {
                          ...(backgroundColor && {
                            background: `${backgroundColor}!important`,
                            '&:hover': {
                              background: `${theme.fn.darken(
                                backgroundColor,
                                0.05,
                              )}`,
                            },
                          }),
                          '&[data-loading]': {
                            '&:before': {
                              background: 'transparent',
                            },
                          },
                        },
                        leftIcon: {
                          display: 'none',
                        },
                      })}
                      onClick={() => getCurrentLocation()}
                      loading={loadingLoc}
                      rightIcon={
                        loadingLoc ? (
                          <Loader color="#fff" size={'xs'} />
                        ) : (
                          <GPS color="#fff" size={18} />
                        )
                      }
                      data-event-category="Service Connection"
                      data-event-action="Find The Nearest Clinic"
                      data-event-label={currentUrl}
                    >
                      {f({
                        id: 'clinicLocator.findNearClinic',
                        defaultMessage: 'Find your nearest Clinic',
                      })}
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown style={{ pointerEvents: 'none' }}>
                    <Text size="xs">
                      {f({
                        id: 'clinicLocator.plsAllowAccess',
                        defaultMessage: 'Please allow location access',
                      })}
                    </Text>
                  </Popover.Dropdown>
                </Popover>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </StyledClinicLocatorContainer>
    </ClinicLocatorContext.Provider>
  )
}

export default ClinicLocator

const TitleComp = ({ title }: { title: string }) => {
  const { isMobile } = useContext(ClinicLocatorContext)
  const { classes, cx } = useStyles({ useMobileLayout: isMobile })
  return (
    <Text
      as="h2"
      miw={0}
      fs={rem(22)}
      className={cx('map-header', classes.titleComp)}
    >
      {title}
    </Text>
  )
}

const ButtonComp = ({
  buttonText,
  backgroundColor,
  specialties_hashtag,
  currentUrl,
}: {
  buttonText: string
  backgroundColor?: string
  specialties_hashtag?: string
  currentUrl: string
}) => {
  const { isMobile } = useContext(ClinicLocatorContext)
  const { classes } = useStyles({ backgroundColor, useMobileLayout: isMobile })
  const buttonSidebarComp = useMemo(() => {
    if (!specialties_hashtag) {
      return null
    }
    const $hashTag = document.getElementById(
      `#${specialties_hashtag.replace(/\#/g, '')}`,
    )
    if (!$hashTag) {
      return null
    }
    const linkProps = {
      'data-event-category': 'Service Connection',
      'data-event-action': 'CLick - View experts',
      'data-event-label': `${currentUrl}${
        specialties_hashtag ? `${specialties_hashtag}` : ''
      }`,
    }
    return (
      <Button
        {...linkProps}
        className={classes.viewExpertBtn}
        size="lg"
        variant="primary"
        onClick={() => {
          $hashTag.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'center',
          })
        }}
        leftIcon={
          <Image
            w={rem(24)}
            h={rem(24)}
            src={`${assetSvgPath}/clinic.svg`}
            alt={buttonText}
          />
        }
      >
        {buttonText}
      </Button>
    )
  }, [specialties_hashtag, buttonText])

  return buttonSidebarComp
}

const StyledClinicLocatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  .g-map {
    width: 100%;
    height: 100%;
  }
`
